import { useCallback, useEffect, useState } from "react";
import { api, apiError, inr } from "@/lib/api";
import { toast } from "sonner";
import { Download, CheckCircle2, AlertTriangle, FileCheck2, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SupplierEmailCell, SupplierRemindDialog, SupplierEmailUpload, SupplierHistoryDialog, HistoryButton } from "@/components/SupplierReminder";

const now = new Date();
const START = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const FYS = Array.from({ length: 6 }, (_, i) => `${START - i}-${String((START - i + 1) % 100).padStart(2, "0")}`);
const TH = "px-2 py-2 text-left";
const THR = "px-2 py-2 text-right";
const TONE = { Matched: "text-emerald-500", Difference: "text-rose-500", "3B not prepared": "text-muted-foreground" };

function Stat({ icon: Icon, label, value, sub, testid, tone }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 small-label"><Icon className={`h-4 w-4 ${tone}`} /> {label}</div>
      <div className="font-mono text-lg mt-1">{value}</div>
      <div className="text-xs text-muted-foreground font-mono">{sub}</div>
    </div>
  );
}

function MonthTable({ rows }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid="availed-month-table">
      <div className="px-4 py-2.5 border-b border-border font-head font-semibold text-sm">Month-wise match — availed invoices vs GSTR-3B Table 4 (net ITC)</div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground border-b border-border"><tr>
          <th className={TH}>Month</th><th className={THR}>Invoices</th><th className={THR}>Availed tax</th><th className={THR}>Net ITC (books)</th>
          <th className={THR}>ITC in GSTR-3B</th><th className={THR}>Difference</th><th className={TH}>3B status</th><th className={TH}>Match</th>
        </tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.period} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5" data-testid={`availed-month-${m.period}`}>
              <td className="whitespace-nowrap">{m.label}</td><td className="text-right">{m.availed_count}</td><td className="text-right">{inr(m.availed_tax)}</td>
              <td className="text-right">{inr(m.net_itc_books)}</td><td className="text-right">{m.itc_3b === null ? "—" : inr(m.itc_3b)}</td>
              <td className={`text-right ${m.difference ? "text-rose-500" : ""}`}>{m.difference === null ? "—" : inr(m.difference)}</td>
              <td className="capitalize">{m.return_status}</td><td className={`whitespace-nowrap ${TONE[m.status]}`}>{m.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceTable({ rows }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid="availed-invoice-table">
      <div className="px-4 py-2.5 border-b border-border font-head font-semibold text-sm">Availed invoices ({rows.length})</div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground border-b border-border"><tr>
          <th className={TH}>Period</th><th className={TH}>Invoice</th><th className={TH}>Date</th><th className={TH}>Supplier</th>
          <th className={TH}>3B table</th><th className={THR}>Taxable</th><th className={THR}>Tax</th><th className={TH}>GSTR-2B</th><th className={TH}>Month match</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.period}-${r.invoice_no}-${i}`} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5">
              <td>{r.period}</td><td className="whitespace-nowrap">{r.invoice_no}</td><td className="whitespace-nowrap">{r.invoice_date}</td>
              <td className="font-sans min-w-[160px]">{r.counterparty_name || r.counterparty_gstin}<div className="font-mono text-[10px] text-muted-foreground">{r.counterparty_gstin}</div></td>
              <td className="whitespace-nowrap">{r.table}</td><td className="text-right">{inr(r.taxable_value)}</td><td className="text-right">{inr(r.tax)}</td>
              <td className={`whitespace-nowrap ${r.twob_period ? "text-emerald-500" : "text-amber-500"}`}>{r.twob_status}{r.twob_period && ` (${r.twob_period})`}</td>
              <td className={`whitespace-nowrap ${TONE[r.match_status]}`}>{r.match_status}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={9} className="px-2 py-4 text-center text-muted-foreground font-sans">No availed invoices in this year</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SupplierTable({ rows, clientId, canEdit, onRemind, onHistory, reload }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid="supplier-followup-table">
      <div className="px-4 py-2.5 border-b border-border flex flex-wrap items-center justify-between gap-2">
        <span className="font-head font-semibold text-sm">Supplier follow-up — claimed but not in GSTR-2B ({rows.length})</span>
        {canEdit && <SupplierEmailUpload clientId={clientId} onDone={reload} />}
      </div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground border-b border-border"><tr>
          <th className={TH}>Supplier</th><th className={THR}>Invoices</th><th className={THR}>Taxable</th><th className={THR}>ITC at risk</th>
          <th className={TH}>Oldest</th><th className={TH}>Invoice nos</th><th className={TH}>Email</th><th className={TH}>Reminder</th>
        </tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.counterparty_gstin || s.counterparty_name} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5" data-testid={`supplier-row-${s.counterparty_gstin || s.counterparty_name}`}>
              <td className="font-sans min-w-[160px]">{s.counterparty_name || s.counterparty_gstin}<div className="font-mono text-[10px] text-muted-foreground">{s.counterparty_gstin || "No GSTIN"}</div></td>
              <td className="text-right">{s.invoice_count}</td><td className="text-right">{inr(s.taxable_value)}</td>
              <td className="text-right text-rose-500 whitespace-nowrap">{inr(s.tax_at_risk)}</td><td>{s.oldest_period}</td>
              <td className="min-w-[160px]">{s.invoice_list}</td>
              <td><SupplierEmailCell key={s.email} clientId={clientId} s={s} canEdit={canEdit && !!s.counterparty_gstin} onSaved={reload} /></td>
              <td className="whitespace-nowrap">
                {canEdit && s.counterparty_gstin && <button data-testid={`supplier-remind-button-${s.counterparty_gstin}`} disabled={!s.email || (s.last_reminded_at && Date.now() - new Date(s.last_reminded_at) < 864e5)} onClick={() => onRemind(s.counterparty_gstin)}
                  title={!s.email ? "Save an email address first" : s.last_reminded_at && Date.now() - new Date(s.last_reminded_at) < 864e5 ? "Already emailed in the last 24 hours" : "Email a GSTR-1 filing request"}
                  className="flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-40"><Mail className="h-3 w-3" /> Email</button>}
                {s.last_reminded_at && <div className="text-[10px] text-muted-foreground" data-testid={`supplier-last-reminded-${s.counterparty_gstin}`}>Sent {new Date(s.last_reminded_at).toLocaleDateString("en-GB").replaceAll("/", "-")} · {s.last_reminded_by}</div>}
                {s.last_reminded_at && <HistoryButton gstin={s.counterparty_gstin} onOpen={onHistory} />}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-2 py-4 text-center text-muted-foreground font-sans">Every claimed invoice is in GSTR-2B</td></tr>}
        </tbody>
      </table>
      <p className="px-4 py-2 text-xs text-muted-foreground">Ask these suppliers to file GSTR-1 / correct their invoices so the credit appears in your GSTR-2B.</p>
    </div>
  );
}

function ReminderLog({ rows, onHistory }) {
  if (!rows?.length) { return null; }
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid="reminder-log-table">
      <div className="px-4 py-2.5 border-b border-border font-head font-semibold text-sm">Reminder log — all suppliers emailed ({rows.length})</div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground border-b border-border"><tr>
          <th className={TH}>Supplier</th><th className={TH}>Email</th><th className={THR}>Requests</th><th className={TH}>Last sent</th><th className={TH}></th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.gstin} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5" data-testid={`reminder-log-row-${r.gstin}`}>
              <td className="font-sans min-w-[160px]">{r.name || r.gstin}<div className="font-mono text-[10px] text-muted-foreground">{r.gstin}</div></td>
              <td>{r.email}</td><td className="text-right">{r.reminder_count}</td>
              <td className="whitespace-nowrap">{new Date(r.last_reminded_at).toLocaleDateString("en-GB").replaceAll("/", "-")} · {r.last_reminded_by}</td>
              <td><button data-testid={`reminder-log-history-${r.gstin}`} onClick={() => onHistory(r.gstin)} className="text-indigo-600 hover:underline">View history</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AvailedItcPanel({ clientId }) {
  const [fy, setFy] = useState(FYS[0]);
  const [rep, setRep] = useState(null);
  const [remind, setRemind] = useState(null);
  const { user } = useAuth();
  const canEdit = user?.role !== "reader";
  const load = useCallback(() => {
    api.get(`/clients/${clientId}/reports/availed-itc?fy=${fy}`).then((r) => setRep(r.data)).catch((e) => toast.error(apiError(e)));
  }, [clientId, fy]);
  useEffect(() => { load(); }, [load]);
  const closeRemind = useCallback(() => setRemind(null), []);
  const [hist, setHist] = useState(null);
  const download = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/reports/availed-itc/export?fy=${fy}`, { responseType: "blob" });
      const m = (res.headers["content-disposition"] || "").match(/filename\*=UTF-8''([^;]+)/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = m ? decodeURIComponent(m[1]) : `${fy}_Availed_ITC_vs_3B.xlsx`; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };
  const s = rep?.summary;
  return (
    <div className="space-y-4" data-testid="availed-itc-panel">
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="availed-itc-fy-select" value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-md bg-card border border-border px-3 py-1.5 text-sm font-mono">
          {FYS.map((f) => <option key={f} value={f}>FY {f}</option>)}
        </select>
        <button data-testid="availed-itc-download-button" onClick={download} className="flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 text-xs font-medium"><Download className="h-3.5 w-3.5" /> Download Excel</button>
        <span className="text-xs text-muted-foreground">Availed = credit taken in Table 4(A) and not reversed. Matched within ₹{rep?.tolerance ?? 1}.</span>
      </div>
      {s && <>
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat icon={FileCheck2} tone="text-indigo-500" label="Availed invoices" value={s.availed_count} sub={`Tax ${inr(s.availed_tax)}`} testid="stat-availed" />
          <Stat icon={s.difference ? AlertTriangle : CheckCircle2} tone={s.difference ? "text-rose-500" : "text-emerald-500"} label="Books vs GSTR-3B" value={inr(s.difference)} sub={`${s.months_matched} matched · ${s.months_difference} with difference`} testid="stat-availed-diff" />
          <Stat icon={AlertTriangle} tone="text-amber-500" label="Availed, not in GSTR-2B" value={s.not_in_2b_count} sub={`At risk ${inr(s.not_in_2b_tax)} · ${s.not_in_2b_suppliers} supplier(s)`} testid="stat-availed-not-2b" />
        </div>
        <MonthTable rows={rep.months} />
        <SupplierTable rows={rep.suppliers_not_in_2b} clientId={clientId} canEdit={canEdit} onRemind={setRemind} onHistory={setHist} reload={load} />
        <SupplierHistoryDialog clientId={clientId} gstin={hist} onClose={() => setHist(null)} />
        <ReminderLog rows={rep.reminded_suppliers} onHistory={setHist} />
        <SupplierRemindDialog clientId={clientId} fy={fy} gstin={remind} onClose={closeRemind} onSent={load} />
        <InvoiceTable rows={rep.invoices} />
      </>}
    </div>
  );
}
